import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';

const AuthContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The session lives in an httpOnly cookie, so there is no token here to attach - the browser
// sends it as long as every request opts into credentials.
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// The session cookie is httpOnly, but the CSRF cookie is readable on purpose: echoing it back
// in a header is what proves the request came from this app. A foreign origin can do neither.
const csrfToken = () =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith('flippex_csrf='))
    ?.split('=')[1];

api.interceptors.request.use((config) => {
  if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
    const token = csrfToken();
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const { getToken } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const [pendingRedirect, setPendingRedirect] = useState(null);

  // Helper to open auth modal with specific tab
  const openAuthModal = (tab = 'login') => {
    setAuthModalTab(tab);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  // JS cannot see the cookie, so the only way to know who is signed in is to ask
  useEffect(() => {
    fetchUser();
  }, []);

  // A session that went cold (4 idle hours) or was signed out elsewhere answers 401 to
  // everything. Drop the user once, tell them why, and let them sign back in.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const isAuthCall = url.includes('/auth/otp/session') || url.includes('/auth/me');
        if (status === 401 && !isAuthCall) {
          setUser((current) => {
            if (current) {
              // Clerk's session lives longer than ours. Left behind, it makes the sign-in screen
              // answer "you're already signed in" and nobody can get back in.
              clerkSignOut().catch(() => {});
              // Pages toast their own "failed to load" in the catch block that runs right
              // after this, so clear the noise a tick later and leave one clear reason.
              setTimeout(() => {
                toast.dismiss();
                toast.error('Your session expired. Please sign in again.');
              }, 80);
              setShowAuthModal(true);
            }
            return null;
          });
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get redirect URL based on user role
  const getRedirectUrl = (userData) => {
    if (!userData) return '/';
    if (userData.role === 'admin') return '/admin';
    return '/app/home';
  };

  // Clerk has just proved the person owns the address; trade that for this app's own session
  // cookie, which is what every screen behind the login actually runs on.
  const completeOtpSession = useCallback(async ({ silent = false } = {}) => {
    try {
      const clerkToken = await getToken({ skipCache: true });
      if (!clerkToken) return { success: false, message: 'Sign-in expired, please request a new code' };

      await api.post('/auth/otp/session', { token: clerkToken });
      const userData = await fetchUser();
      if (!silent) {
        toast.success(`Welcome${userData?.full_name ? `, ${userData.full_name.split(' ')[0]}` : ''}!`);
      }
      let redirectUrl = pendingRedirect || getRedirectUrl(userData);
      // A first-time investor goes to onboarding instead of a dashboard with nothing on it.
      // Admins never see it, and anyone who already has a profile skips straight through.
      if (!pendingRedirect && userData?.role !== 'admin') {
        try {
          const { data } = await api.get('/onboarding/profile/state');
          if (data?.needs_onboarding) redirectUrl = '/app/onboarding';
        } catch (error) {
          // if we cannot tell, send them to the dashboard rather than block the sign-in
        }
      }
      return { success: true, redirectUrl, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'We could not finish signing you in';
      if (!silent) toast.error(message);
      return { success: false, message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, pendingRedirect]);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // the cookie is cleared server-side either way; never trap someone on a signed-in screen
    }
    try {
      await clerkSignOut();
    } catch (error) {
      // signing out of Clerk is a courtesy; the app session is already gone
    }
    setUser(null);
    setPendingRedirect(null);
    toast.success('Logged out successfully');
  };

  const clearPendingRedirect = () => {
    setPendingRedirect(null);
  };

  // Helper to open auth modal with specific tab and optional redirect URL
  const openAuthModalWithRedirect = (tab = 'login', redirectUrl = null) => {
    setAuthModalTab(tab);
    if (redirectUrl) {
      setPendingRedirect(redirectUrl);
    }
    setShowAuthModal(true);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        completeOtpSession,
        logout,
        showAuthModal,
        setShowAuthModal,
        authModalTab,
        openAuthModal,
        openAuthModalWithRedirect,
        closeAuthModal,
        pendingRedirect,
        setPendingRedirect,
        clearPendingRedirect,
        getRedirectUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
